import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Grid,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  MenuItem,
  Select,
} from "@mui/material";
import Fade from "@mui/material/Fade";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import appData from "layouts/orders/data/appData";
import ProductDetailsDialog from "./productDetailsDialog";
import { formatSAR } from "../../helper/format";
import { useMediaQuery } from "@mui/material";

function OrdersDashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const isMobile = useMediaQuery("(max-width:600px)");

  const {
    columns,
    rows,
    notification,
    setNotification,
    isModalOpen,
    fetchProducts,
    handleCloseModal,
    handleCloseNotification,
    handleCloseEditModal,
    isEditModalOpen,
    editedOrder,
    handleSaveChanges,
    setEditedOrder,
    orders,
    isPaymentVerificationModalOpen,
    handleClosePaymentVerificationModal,
    handleAcceptPayment,
    handleRejectPayment,
    selectedPaymentOrder,
  } = appData();

  useEffect(() => {
    setLoading(true);

    if (!orders || orders.length === 0) {
      return;
    }

    try {
      const filteredOrders = orders.filter((order) => {
        const status = order.orderStatus || "";
        const paymentMethod = order.paymentMethod || "";

        return (
          (filter === "all" || filter === "" || status === filter) &&
          (paymentMethodFilter === "all" ||
            paymentMethod.toLowerCase().includes(paymentMethodFilter.toLowerCase()))
        );
      });
      const stats = {
        totalorders: filteredOrders.length,
        paidorders: filteredOrders.filter((order) => order.orderStatus === "Paid").length,
        pendingorders: filteredOrders.filter((order) => order.orderStatus === "Pending").length,
        cancelledorders: filteredOrders.filter((order) => order.orderStatus === "Cancelled").length,
      };

      setStats(stats);
    } catch (error) {
      console.error("Error calculating stats:", error);
      setNotification({
        open: true,
        message: t("orders.statsError"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [orders]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const status = row.orderStatus?.props?.badgeContent || "";
      const paymentMethod = row.paymentMethod || "";
      return (
        (filter === "all" || status.toLowerCase().includes(filter.toLowerCase())) &&
        (paymentMethodFilter === "all" ||
          paymentMethod.toLowerCase().includes(paymentMethodFilter.toLowerCase()))
      );
    });
  }, [rows, filter, paymentMethodFilter]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} marginBottom={5}>
          {[
            {
              title: t("orders.stats.totalOrders"),
              count: stats.totalorders,
              color: "info",
              icon: "shopping_cart",
            },
            {
              title: t("orders.stats.paidOrders"),
              count: stats.paidorders,
              color: "success",
              icon: "done",
            },
            {
              title: t("orders.stats.pendingOrders"),
              count: stats.pendingorders,
              color: "warning",
              icon: "hourglass_empty",
            },
            {
              title: t("orders.stats.cancelledOrders"),
              count: stats.cancelledorders,
              color: "error",
              icon: "cancel",
            },
          ].map(({ title, count, color, icon }) => (
            <Grid item xs={12} md={6} lg={3} key={title}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color={color}
                  icon={icon}
                  title={title}
                  count={loading ? <CircularProgress size={20} /> : count}
                  percentage={{ color: "success", amount: "", label: t("orders.justUpdated") }}
                />
              </MDBox>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="dark"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="space-between"
                alignItems={isMobile ? "flex-start" : "center"}
              >
                <MDTypography variant="h6" color="white">
                  {t("orders.title")}
                </MDTypography>
                <div
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    gap: "10px",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  {/* Order Status Filter */}
                  <Select
                    value={filter || "all"}
                    onChange={(e) => setFilter(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{
                      padding: "11px",
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                      borderRadius: "6px",
                      color: "white",
                      minWidth: isMobile ? "100%" : "150px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ffffff" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e88e5" },
                    }}
                  >
                    <MenuItem value="all">{t("orders.filters.allStatuses")}</MenuItem>
                    <MenuItem value="Paid">{t("orders.statuses.paid")}</MenuItem>
                    <MenuItem value="Pending">{t("orders.statuses.pending")}</MenuItem>
                    <MenuItem value="Cancelled">{t("orders.statuses.cancelled")}</MenuItem>
                    <MenuItem value="Completed">{t("orders.statuses.completed")}</MenuItem>
                    <MenuItem value="InProgress">{t("orders.statuses.inProgress")}</MenuItem>
                    <MenuItem value="Delivered">{t("orders.statuses.delivered")}</MenuItem>
                  </Select>

                  {/* Payment Method Filter */}
                  <Select
                    value={paymentMethodFilter || "all"}
                    onChange={(e) => setPaymentMethodFilter(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{
                      padding: "11px",
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                      borderRadius: "6px",
                      color: "white",
                      minWidth: isMobile ? "100%" : "150px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ffffff" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e88e5" },
                    }}
                  >
                    <MenuItem value="all">{t("orders.filters.allMethods")}</MenuItem>
                    <MenuItem value="card">{t("orders.paymentMethods.card")}</MenuItem>
                    <MenuItem value="stcpay">{t("orders.paymentMethods.stcpay")}</MenuItem>
                    <MenuItem value="applepay">{t("orders.paymentMethods.applepay")}</MenuItem>
                    <MenuItem value="mada">{t("orders.paymentMethods.mada")}</MenuItem>
                    <MenuItem value="mastercard">{t("orders.paymentMethods.mastercard")}</MenuItem>
                    <MenuItem value="bank">{t("orders.paymentMethods.bank")}</MenuItem>
                  </Select>
                </div>
              </MDBox>

              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows: filteredRows }}
                  isSorted
                  entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                  showTotalEntries
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Footer />

      {/* Edit Order Dialog */}
      <Dialog
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
      >
        <DialogTitle>{t("orders.updateOrderStatus")}</DialogTitle>
        <DialogContent>
          {editedOrder && (
            <Select
              name="orderState"
              value={editedOrder.orderStatus ? editedOrder.orderStatus : ""}
              onChange={(e) => setEditedOrder((prev) => ({ ...prev, orderStatus: e.target.value }))}
              fullWidth
              margin="dense"
              sx={{
                marginTop: "1px",
                padding: "11px",
                backgroundColor: "rgba(0, 0, 0, 0.1)",
                borderRadius: "6px",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0, 0, 0, 0.3)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#1e88e5" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e88e5" },
              }}
            >
              <MenuItem value="Paid">{t("orders.statuses.paid")}</MenuItem>
              <MenuItem value="Pending">{t("orders.statuses.pending")}</MenuItem>
              <MenuItem value="Cancelled">{t("orders.statuses.cancelled")}</MenuItem>
              <MenuItem value="Completed">{t("orders.statuses.completed")}</MenuItem>
              <MenuItem value="InProgress">{t("orders.statuses.inProgress")}</MenuItem>
              <MenuItem value="Delivered">{t("orders.statuses.delivered")}</MenuItem>
            </Select>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} color="secondary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSaveChanges} color="primary">
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <MDSnackbar
        color={notification.severity}
        icon={notification.severity === "success" ? "check" : "error"}
        title={notification.severity === "success" ? t("common.success") : t("common.error")}
        content={notification.message}
        open={notification.open}
        onClose={handleCloseNotification}
        close={handleCloseNotification}
        bgWhite
      />

      {/* Product Details Dialog */}
      <ProductDetailsDialog
        isOpen={isModalOpen}
        handleClose={handleCloseModal}
        fetchProducts={fetchProducts}
      />

      {/* Payment Verification Dialog */}
      <Dialog
        open={isPaymentVerificationModalOpen}
        onClose={handleClosePaymentVerificationModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("orders.paymentVerification.title")}</DialogTitle>
        <DialogContent>
          {selectedPaymentOrder && (
            <MDBox>
              <MDTypography variant="h6" mb={2}>
                {t("orders.paymentVerification.paymentImage")}
              </MDTypography>
              <img
                src={selectedPaymentOrder.paymentImage}
                alt={t("orders.paymentVerification.paymentProof")}
                style={{
                  width: "100%",
                  maxHeight: "400px",
                  objectFit: "contain",
                  marginBottom: "20px",
                }}
              />
              <MDTypography variant="body2" color="text.secondary">
                {t("orders.paymentVerification.orderAmount")}:{" "}
                {formatSAR(selectedPaymentOrder.totalAmount)}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentVerificationModal} color="secondary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleRejectPayment} color="error">
            {t("orders.paymentVerification.reject")}
          </Button>
          <Button onClick={handleAcceptPayment} color="success">
            {t("orders.paymentVerification.accept")}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default OrdersDashboard;
